import { Component, DestroyRef, ElementRef, Input, OnInit, effect, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { EntryService } from '../entry.service';
import { UtilService } from '../../util.service';
import { SummaryText, SummaryData, TedDomain, ProviderData } from '../structure-data.models';
import { EntryData, PlddtData, Structure } from '../../entry/structure-data.models';
import { GoogleAnalyticsService } from '../../google-analytics.service';
import { CommonService } from '../../common.service';
import { ConfigService } from '../../config.service';
import { PlatformService } from '../../platform.service'; 
import { MolstarPluginService } from "../../molstar-plugin-service";
import {
  applyAlphaMissenseTooltips,
  colorByAlphaMissense,
  resetColoring,
  resetTooltips
} from '../../molstar-alpha-missense';
import { colorByTed } from '../../molstar-alpha-ted';
import { catchError, combineLatest, distinctUntilChanged, filter, map, of, take, tap } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'summary-tab',
  templateUrl: './summary-tab.component.html',
  styleUrl: './summary-tab.component.css',
  standalone: false
})
export class SummaryTabComponent implements OnInit  {

  constructor(
    private store: Store,
    private entryService: EntryService,
    private utilService: UtilService,
    public commonService: CommonService,
    private gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private platform: PlatformService,
    private el: ElementRef,
    private molstarPluginService : MolstarPluginService,
  ) {}

  public data: SummaryData;
 
  public pdbeMolstar: any;
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  public entryData = signal<EntryData>({} as EntryData);
  public uniprotData = signal<any>({});
  public tedDomainData = toSignal(this.store.select(EntrySelectors.tedDomainData));
  public csvAmData = toSignal(this.store.select(EntrySelectors.csvAmData));

  public providerData = signal<ProviderData>({} as ProviderData);
  
  public isTedDomain = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public isCopySequence: boolean = false;
  
  public infoSectionHeight: any = { maxHeight: '1000px', overflow: 'hidden' };
  public showInfoSection = true;
  public kbCount = 0;
  public summaryTexts: any[][] = [];
  private summaryGroupSize = 5; // or 5, or any number

  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  public isAlphaMis: boolean = false;
  public isModalConf: boolean = true;

  public rangeFilter: any;
  /* AlphaMissense Variables */
  public csvAMFileUrl: string;
  public hydrationReady: boolean =  false;
  public plddtData: PlddtData;
  public isOldUniprotEntry: boolean = false;
  public otherOrganismScientificNames;

  ngOnInit(): void {
    combineLatest([
      this.store.select(EntrySelectors.entryData),
      this.store.select(EntrySelectors.uniprotData),
      this.store.select(EntrySelectors.providerData),
    ])
    .pipe(
      map(([entryData, uniprotData, providerData]) => {
        this.entryData.set(entryData);
        this.uniprotData.set(uniprotData);
        this.providerData.set(providerData);
        this.createSummaryData();
        this.getPdbeData();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
     
    this.initMolstar();
     
    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }
  }

  public summaryGroup(arr: any[], size: number): any[][] {
   
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  getShortenedText(text: string): string {
    if (!text) {
      return;
    }
    const words = text.split(' ');
    if (words.length <= this.data.text_limit) {
      return text;
    }
    const wordSubset = words.slice(0, this.data.text_limit);
    return wordSubset.join(' ') + ' ...';
  }

  getShortenedOrganismNames(text: string): string {
    if (!text) {
      return '';
    }
    if (text.length <= 60) {
      return text;
    }
    return text.substring(0, 60) + ' ...';
  }

  // Check if "Show more" link is needed
  checkOrganismHasToShowMore(text: SummaryText): boolean {
    return !text.show_long && text.text?.length > 60;
  }

  async toggleAccordion(data){
    await this.applyMolstarColoring(data.type);
  }

  toggleUpdateFlags(data) {
    this.isAlphaMis = data.isAlphaMis;
    this.isModalConf = data.isModalConf;
  }

  /**
   * Toggle if a text should be shown completely or only in a shortened
   * version
   * @param text - a text object that has a .show_long attribute
   */
  switchText(text: SummaryText): boolean {
    text.show_long = !text.show_long;
    return text.show_long;
  }

  /**
   * Check if a text is longer than the limit
   * @param text - a text object
   */
  checkIfHasToShowMore(text: SummaryText): boolean {
    const words = text.text.split(' ');
    return !text.show_long && words.length >= this.data.text_limit;
  }

  toggleInfoSection(): void {
    if (this.showInfoSection) {
      this.infoSectionHeight = Object.assign(
        {},
        {
          maxHeight: '0px',
          marginTop: '0px',
          paddingBottom: '0px',
          overflow: 'hidden',
        }
      );
    } else {
      this.infoSectionHeight = Object.assign(
        {},
        { maxHeight: '1000px', paddingBottom: '16px', overflow: 'hidden' }
      );
    }
    this.showInfoSection = !this.showInfoSection;
  }

  applyMolstarColoring(kind: 'plddt' | 'missense') {
    if (kind === 'missense') {
      this.isAlphaMis = true;
      this.isModalConf = false;
      // colorByAlphaMissense(this.pdbeMolstar, this.csvAmData);
      if (this.rangeFilter) {
        colorByAlphaMissense(
          this.pdbeMolstar,
          this.csvAmData(),
          this.rangeFilter
        );
      } else {
        colorByAlphaMissense(this.pdbeMolstar, this.csvAmData());
      }
      applyAlphaMissenseTooltips(this.pdbeMolstar, this.csvAmData());
      this.gaService.eventEmitter(
        'AM_toggle',
        'AlphaMissense',
        'click',
        'AM_toggle',
        'Toggle between AM and pLDDT colouring on Mol*'
      );
    } else if (kind === 'plddt') {
      this.isAlphaMis = false;
      this.isModalConf = true;
      resetColoring(this.pdbeMolstar);
      resetTooltips(this.pdbeMolstar);
    } else if (kind === 'ted') {
      this.setUpTEDToggleUI();
      colorByTed(this.pdbeMolstar, this.tedDomainData());
      this.gaService.eventEmitter(
        'domains_toggle',
        'domains',
        'click',
        'domains_toggle',
        'Toggle between TED colouring on Mol*'
      );
    }  else {
      console.error('Unknow coloring kind:', kind);
    }
  }
  private setUpTEDToggleUI(): void {
    this.isAlphaMis = false;
    this.isModalConf = false;
    this.isTedDomain.set(true);
  }

  async clearSeqSelection(): Promise<void> {
    // const selectedSeqElements = this.el.nativeElement.querySelectorAll(
    //   '.ms-pae-selection, .ms-pae-select-green, .ms-pae-select-orange, .ms-pae-select-yellow'
    // );
    // if (selectedSeqElements) {
    //   selectedSeqElements.forEach((selEle) => {
    //     selEle.classList.remove('ms-pae-selection');
    //     selEle.classList.remove('ms-pae-select-green');
    //     selEle.classList.remove('ms-pae-select-orange');
    //     selEle.classList.remove('ms-pae-select-yellow');
    //   });
    // }
    await this.pdbeMolstar.visual.clearSequenceColor();
  }

  // applySeqSelection(selectionData) {
  //   this.clearSeqSelection();
  //   selectionData.forEach((ele) => {
  //     for (
  //       let seqid = ele.start_residue_number;
  //       seqid <= ele.end_residue_number;
  //       seqid++
  //     ) {
  //       const seqElement = this.el.nativeElement.querySelector(
  //         `.msp-sequence-present[data-seqid="${seqid}"`
  //       );
  //       if (seqElement) {
  //         seqElement.classList.add(`${ele.class}`);
  //       }
  //     }
  //   });
  // }

  validateBlankSelection(regions) {
    regions.forEach((select) => {
      if (select.start === 0 && select.end === 0) {
        this.clearSeqSelection();
        this.pdbeMolstar.visual.reset({ camera: true });
      }
    });
    return;
  }


  validateRegions = (regions) => {
    let valid = true;
    for (const region of regions) {
      if (region.start === 0 && region.end === 0) {
        valid = false;
        return;
      }
    }
    return valid;
  };

  cmSelectionUpdate(cmSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    const validRegions = this.validateRegions(regions);

    if (!validRegions) {
      await this.applyMolstarColoring(this.isAlphaMis ? 'missense' : 'plddt');
      await this.clearSeqSelection();
      this.pdbeMolstar.visual.reset({ camera: true });
      return;
    }

    let adjustedRegions = [];
    for (const region of regions) {
      if (region.start === 0) {
        region.start = 1;
      }
      if (region.end === 0) {
        region.end = 1;
      }
      adjustedRegions.push(region);
    }

    const selectionData = this.utilService.getSelectionData(adjustedRegions);

    await this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });
    await this.pdbeMolstar.visual.sequenceColor({
      data: selectionData,
    });

    // this.applySeqSelection(selectionData);
  }

  initMolstar(){
    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };
   
    // Set last updated date
    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString(
      'default',
      { month: 'long' }
    )} ${modelCreatedDate.getFullYear()}`;
    setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 100);
  }

  async handleMolstar(structure: Structure): Promise<void> {
    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      rightPanel: true,
      // leftPanel: false,
      hideControls: false,
      validationAnnotation: true,
      granularity: 'residue'
    };

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
    } else {
      const ele = this.el.nativeElement.querySelector('.summary-molstar-container');
      const eleSeq = this.el.nativeElement.querySelector('.summary-sequencepanel');
      
      if (ele) {
        // this.pdbeMolstar = new PDBeMolstarPlugin();
        await this.molstarPluginService.loadPlugin();
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          {target: ele, component:this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
          {target: eleSeq, component:this.molstarPluginService.getClass().UIComponents.SequenceView },
        ]
        this.pdbeMolstar.render(layout, options);
        // this.pdbeMolstar.render(ele, options);

        // Update Mol load complete flag
        this.pdbeMolstar.events.loadComplete.subscribe((e) => {
          this.molLoadComplete = true;
        });
      }
    }
  }

  getPdbeData() { 
    if(this.entryData().isUniProt === true){
      const accessionName = this.entryData().uniprotAccession;
   
      // this is the url which we ther from start
      // const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&group.facet=true&facet=true&rows=0&facet.limit=5&facet.offset=0&facet.mincount=1&facet.sort=count&omitHeader=true&json.nl=map&facet.field=uniprot_accession&facet.threads=-1&q=uniprot_accession:${accessioName}&wt=json`;
       
      // below is the url shared by Sameer 
      const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${accessionName}&wt=json`;
  
      this.commonService.getApiData(solrApiUrl).pipe(
        map((apiData) => {
          if (apiData && apiData.grouped && apiData.grouped.pdb_id) {
            this.kbCount = apiData.grouped.pdb_id.ngroups;
            this.uniprotSummaryData();
          }
        }),
        catchError((error) => {
          console.error('Error fetching pdbe data:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
  
      ).subscribe();
    }else{
      this.kbCount = 0;
      this.uniprotSummaryData();
    }
  }

  createSummaryData(): void {
    const dataItem = this.entryData();

    // const tempOtherOrgamism = ["Tyrannosaurus rex","Arabidopsis thaliana","Herrania umbratica"];
    this.otherOrganismScientificNames = {
      text:  dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.join(", ") : "", //tempOtherOrgamism.join(", "),
      totalCount: dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.length : 0,  //tempOtherOrgamism.length,
      italic: false,
      show_long: false,
      source: '',
      source_url: '',
    }
    const summary = {
      title: '',
      subtitle: '',
      texts: [],
      text_limit: 20,
      flags: [],
      isUniProtReferenceProteome: false,
      isUniProtReviewed: false,
      afdb_accession: ''
    };

    summary.title = dataItem.uniprotDescription;
    summary.subtitle = 'AlphaFold structure prediction';
    summary.isUniProtReferenceProteome = dataItem.isUniProtReferenceProteome;
    summary.isUniProtReviewed = dataItem.isUniProtReviewed;
    summary.afdb_accession = `${dataItem.modelEntityId}-v${dataItem.latestVersion}`;

    if (dataItem.modelEntityId) {
      summary.texts.push({
        label: 'Protein',
        text: dataItem.uniprotDescription ? dataItem.uniprotDescription : 'Not named',
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.modelEntityId) {
      const geneName = dataItem.gene ? dataItem.gene : 'Not available';
      summary.texts.push({
        label: 'Gene',
        text: geneName,
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }

    // Add common name after latin name, if available
    if (dataItem.organismScientificName) {
      summary.texts.push({
        label: 'Source organism',
        text: dataItem.organismScientificName,
        searchText: dataItem.organismScientificName,
        italic: true,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.uniprotAccession) {
      summary.texts.push({
        label: 'UniProt',
        text: dataItem.uniprotAccession,
        italic: false,
        show_long: true,
        source: 'UniProt',
        source_url:
          this.configService.getConfig().uniprotEntryUrl +
          dataItem.uniprotAccession,
      });
    }
    if (dataItem.globalMetricValue) {
      const averagepLDDT = dataItem.globalMetricValue ? `${dataItem.globalMetricValue} (${this.getPlddtLevel(dataItem.globalMetricValue)})` : 'Not available';
      summary.texts.push({
        label: 'Average pLDDT',
        text: averagepLDDT,
        italic: false,
        show_long: true,
        source: 'tooltip',
        source_url: '',
      });
    }
    if (dataItem.fractionPlddtVeryLow || dataItem.fractionPlddtLow || dataItem.fractionPlddtConfident || dataItem.fractionPlddtVeryHigh) {
      this.plddtData = {
        fractionPlddtVeryLow: this.entryData().fractionPlddtVeryLow,
        fractionPlddtLow: this.entryData().fractionPlddtLow,
        fractionPlddtConfident: this.entryData().fractionPlddtConfident,
        fractionPlddtVeryHigh: this.entryData().fractionPlddtVeryHigh
      };
    }
   
    this.data = summary;
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
    // this.uniprotSummaryData()
  }

  uniprotSummaryData(): void {
    
    // Check if common name exists in UniProt data
    let commonName = '';
    if (
      this.uniprotData() &&
      this.uniprotData().organism &&
      this.uniprotData().organism.names
    ) {
      const names = this.uniprotData().organism.names;
      names.forEach((name) => {
        if (name.type && name.type === 'common') {
          commonName = name.value;
        }
      });
      const sourceOrganism = this.data.texts[2];
      sourceOrganism.text = commonName
        ? sourceOrganism.text + ' (' + commonName + ')'
        : sourceOrganism.text;
    }

    // Default biological function text
    const bioFunction: SummaryText = {
      label: 'Biological function',
      text: 'Data unavailable',
      italic: false,
      show_long: false,
      source: 'tooltip',
      source_url: '',
      isTooltip: true
    };
    let catalyticActivity: string;
    // Set biological function and catalytic activity
    if (this.uniprotData() && this.uniprotData().comments) {
      if(this.uniprotData().comments[0].text){
        bioFunction.text = this.uniprotData().comments[0].text[0].value;
        bioFunction.text += '.';
        bioFunction.source = 'UniProt'
        bioFunction.source_url = "https://www.uniprot.org/uniprot/"+this.uniprotData().accession;
      }
     
      this.uniprotData().comments.forEach((comment) => {
        // if (comment.type === 'FUNCTION') {
        //   bioFunction.text = comment.text[0].value;
        // }
        if (comment.type === 'CATALYTIC_ACTIVITY') {
          catalyticActivity = comment.reaction.name;
        }
      });
    }
    
    if (bioFunction.text === 'Data unavailable' && catalyticActivity !== undefined) {
      bioFunction.text = 'Catalytic activity: ' + catalyticActivity;
    }
   
    if (bioFunction.text !== 'Data unavailable') {
      const pubmed = 'PubMed:([0-9]+)';
      const re = new RegExp(pubmed, 'g');
      bioFunction.text = bioFunction.text.replace(
        re,
        '<a href="https://www.uniprot.org/citations/$1" target="_blank">PubMed:$1</a>'
      );
    }
   
    // Add KB data
    const kbText =
      this.kbCount === 0
        ? 'None available in the PDB'
        : `${this.kbCount} structure${this.kbCount > 1 ? 's' : ''} in PDB for ${
          this.entryData().uniprotAccession
        }`;
    const kbSource = this.kbCount === 0 ? '' : 'PDBe-KB';
    const kbUrl =
      this.kbCount === 0
        ? ''
        : this.configService.getConfig().pdbeKbEntryUrl + this.uniprotData().accession;
    const kbdata = {
      label: 'Experimental structures',
      text: kbText,
      italic: false,
      show_long: true,
      source: kbSource,
      source_url: kbUrl,
    };
   
    const kbidx = this.data.texts.findIndex((o) => o.label === 'Experimental structures');

    if (kbidx !== -1) {
      // remove from old position
      this.data.texts.splice(kbidx, 1);
    }

    
    if (this.data.texts.some(item => item.label === 'Biological function')) {
      return; // Stop execution if 'Biological function' is found
    }
    this.data.texts.splice(4, 0, bioFunction);

    // insert at 6th position (index 5)
    this.data.texts.splice(5, 0, { ...kbdata });
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
  }

  public copySequence(sequence) {
    const text = `${sequence}`;
    this.utilService.copy(text);
    this.isCopySequence = true;
    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);
    this.gaService.eventEmitter('sequence_copy', 'interaction', 'click', "copy_sequence", "Click on Copy sequence");
  }
  
 public getPlddtLevel(plddt: number): string {
    return plddt > 90 ? 'Very High' :
           plddt > 70 ? 'High' :
           plddt > 50 ? 'Low' :
           'Very Low';
  }
}
